import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, Brain, Lightbulb, GraduationCap, Heart, Repeat, Sparkles,
  Users, Moon, Zap, ArrowLeft, ArrowRight, Send, RefreshCw, BookOpen, ExternalLink,
  AlertCircle, Trash2, Lock,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  target: Target, brain: Brain, lightbulb: Lightbulb, 'graduation-cap': GraduationCap,
  heart: Heart, repeat: Repeat, sparkles: Sparkles, users: Users, moon: Moon, zap: Zap,
};

export default function CognoGuruChat() {
  const { guruId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [guru, setGuru] = useState(null);
  const [topics, setTopics] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null);
  const [showTopicLib, setShowTopicLib] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) {
      const redirectUrl = window.location.origin + window.location.pathname;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    loadGuru();
    loadConversation();
    loadQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guruId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const loadGuru = async () => {
    try {
      const r = await axios.get(`${API}/cognoguru/gurus/${guruId}`);
      setGuru(r.data.guru);
      setTopics(r.data.topics || []);
    } catch {
      toast.error('Guru not found');
      navigate('/cognoguru');
    }
  };

  const loadConversation = async () => {
    try {
      const r = await axios.get(`${API}/cognoguru/conversations/${guruId}`, { withCredentials: true });
      setMessages(r.data.messages || []);
    } catch { /* empty conv */ }
  };

  const loadQuota = async () => {
    try {
      const r = await axios.get(`${API}/cognoguru/quota`, { withCredentials: true });
      setQuota(r.data);
    } catch { /* ignore */ }
  };

  const sendMessage = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput('');
    setSending(true);

    // Optimistically render the user message
    const optimistic = { role: 'user', content: q, timestamp: new Date().toISOString(), msg_id: 'tmp-' + Date.now() };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const r = await axios.post(
        `${API}/cognoguru/chat`,
        { guru_id: guruId, question: q },
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, r.data.assistant_message]);
      setQuota({ ...quota, ...r.data.quota, used: (quota?.used ?? 0) + 1 });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Something went wrong';
      // Roll back the optimistic message
      setMessages((prev) => prev.filter((m) => m.msg_id !== optimistic.msg_id));
      if (status === 429) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const resetConversation = async () => {
    if (!confirm('Clear all messages with this Guru? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/cognoguru/conversations/${guruId}`, { withCredentials: true });
      setMessages([]);
      toast.success('Conversation cleared');
    } catch { toast.error('Could not clear conversation'); }
  };

  if (!guru) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="cognoguru-chat-loading">
        <p className="mono-data text-zinc-400">Loading Guru...</p>
      </div>
    );
  }

  const Icon = ICON_MAP[guru.icon] || Brain;
  const isExhausted = quota && quota.remaining === 0 && !quota.is_pro;

  return (
    <div className="min-h-screen bg-white" data-testid="cognoguru-chat">
      {/* Header */}
      <div className="border-b border-[#09090B] bg-[#09090B] text-white">
        <div className="container-swiss py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-12 h-12 border border-white flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="mono-data text-[10px] text-zinc-400">CognoGuru Safe™</p>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="guru-name">
                  {guru.name}
                </h1>
                <p className="mono-data text-xs text-zinc-400 mt-0.5">{guru.tagline}</p>
              </div>
            </div>
            <Link to="/cognoguru" className="flex items-center gap-1 text-xs mono-data text-zinc-300 hover:text-white whitespace-nowrap" data-testid="back-to-council">
              <ArrowLeft className="w-3 h-3" /> Council
            </Link>
          </div>
        </div>
      </div>

      {/* Quota strip */}
      {quota && (
        <div className="border-b border-[#09090B] bg-zinc-50">
          <div className="container-swiss py-2 flex items-center justify-between gap-3">
            <p className="mono-data text-[10px] text-zinc-500" data-testid="quota-strip">
              {quota.is_pro ? (
                <>PRO · Unlimited questions</>
              ) : (
                <>FREE TIER · {quota.remaining} of {quota.limit} questions remaining today</>
              )}
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowTopicLib((v) => !v)} className="mono-data text-[10px] text-[#09090B] hover:underline flex items-center gap-1" data-testid="toggle-topic-lib">
                <BookOpen className="w-3 h-3" /> {showTopicLib ? 'Hide' : 'Topics'} library
              </button>
              {messages.length > 0 && (
                <button onClick={resetConversation} className="mono-data text-[10px] text-zinc-500 hover:text-[#09090B] flex items-center gap-1" data-testid="reset-conv">
                  <Trash2 className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container-swiss grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Chat column */}
        <div className="lg:col-span-8 lg:border-r border-[#09090B] flex flex-col" style={{ minHeight: 'calc(100vh - 220px)' }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="border border-[#09090B] p-6 bg-zinc-50">
                <p className="mono-data text-[#09090B] mb-2">Welcome message</p>
                <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                  Hi — I'm <strong>{guru.name}</strong>. {guru.long_description}
                </p>
                <p className="mono-data text-[10px] text-zinc-500 mb-3">Suggested questions:</p>
                <div className="space-y-2">
                  {guru.suggested_prompts.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      disabled={sending || isExhausted}
                      className="block w-full text-left text-sm border border-zinc-300 hover:border-[#09090B] hover:bg-white p-3 transition-colors disabled:opacity-50"
                      data-testid="suggested-prompt"
                    >
                      → {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <ChatBubble key={m.msg_id} message={m} guruName={guru.name} />
            ))}

            {sending && (
              <div className="flex items-center gap-2 mono-data text-xs text-zinc-400" data-testid="thinking-indicator">
                <RefreshCw className="w-3 h-3 animate-spin" /> {guru.name} is thinking...
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-[#09090B] p-4 bg-white">
            {isExhausted ? (
              <div className="border border-[#09090B] p-4 bg-zinc-50 flex items-start gap-3" data-testid="quota-exhausted">
                <Lock className="w-4 h-4 text-[#09090B] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#09090B] mb-1">Daily free questions used</p>
                  <p className="text-xs text-zinc-600 mb-3">You've hit the {quota.limit}-question daily limit. Pro members chat with all 10 CognoGurus without limits.</p>
                  <Link to="/pricing" className="inline-flex items-center gap-2 bg-[#09090B] text-white px-4 py-2 text-xs font-bold tracking-widest uppercase hover:bg-zinc-800">
                    Upgrade to Pro <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-end gap-2"
                data-testid="chat-form"
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={`Ask ${guru.name} something...`}
                  rows={2}
                  disabled={sending}
                  className="flex-1 input-swiss text-sm resize-none"
                  data-testid="chat-input"
                  maxLength={2000}
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="bg-[#09090B] text-white px-4 py-3 hover:bg-zinc-800 disabled:opacity-40 transition-colors flex items-center gap-2"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
            <p className="mono-data text-[9px] text-zinc-400 mt-2 leading-relaxed" data-testid="safety-footer">
              {guru.name} provides educational guidance only — it doesn't diagnose, treat, or replace professional care. For urgent help: India iCall 9152987821 · US 988.
            </p>
          </div>
        </div>

        {/* Side: Guru topics library */}
        <aside className={`lg:col-span-4 lg:block ${showTopicLib ? 'block' : 'hidden lg:block'}`} data-testid="topic-library">
          <div className="p-4 sm:p-6">
            <p className="mono-data text-[#09090B] mb-2">Topic Library</p>
            <h3 className="text-lg font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What {guru.name} knows
            </h3>
            <div className="space-y-3">
              {topics.map((t) => (
                <div key={t.topic_id} className="border border-zinc-200 p-3 hover:border-[#09090B] transition-colors" data-testid={`topic-${t.topic_id}`}>
                  <p className="mono-data text-[10px] text-zinc-400 mb-1">{t.subtopic}</p>
                  <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.topic_name}</h4>
                  <p className="text-xs text-zinc-600 leading-relaxed mb-2 line-clamp-3">
                    {t.concept_explanation}
                  </p>
                  <button
                    onClick={() => sendMessage(`Tell me more about: ${t.topic_name}`)}
                    disabled={sending || isExhausted}
                    className="mono-data text-[10px] text-[#09090B] hover:underline disabled:opacity-50"
                  >
                    Ask about this →
                  </button>
                  {t.source_url && (
                    <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="mono-data text-[10px] text-zinc-400 hover:text-[#09090B] mt-1 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {t.source_type?.replace(/_/g, ' ')}
                    </a>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-zinc-50 border-l-2 border-[#09090B]">
              <p className="text-[10px] text-zinc-600 leading-relaxed flex items-start gap-2">
                <AlertCircle className="w-3 h-3 text-[#09090B] mt-0.5 flex-shrink-0" />
                Every claim is sourced from peer-reviewed research or recognized expert bodies (NIH, APA, Harvard, Stanford, WHO).
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}


function ChatBubble({ message, guruName }) {
  const isUser = message.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[85%] bg-[#09090B] text-white p-3 sm:p-4">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex" data-testid="msg-assistant">
      <div className="max-w-[95%] border border-[#09090B] p-4 sm:p-5 bg-white">
        <p className="mono-data text-[10px] text-[#09090B] mb-3">{guruName}</p>
        <div className="prose prose-sm max-w-none text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {message.content}
        </div>
        {message.citations?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-zinc-200">
            <p className="mono-data text-[9px] text-zinc-400 mb-2">Cited sources</p>
            <div className="flex flex-wrap gap-2">
              {message.citations.map((c, i) => (
                <a
                  key={i}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] mono-data border border-zinc-300 px-2 py-1 hover:border-[#09090B] inline-flex items-center gap-1"
                  data-testid={`citation-${i}`}
                >
                  <ExternalLink className="w-3 h-3" /> {c.domain}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
